<template>
    <div
        id="quiz-creator-log-sidebar"
        :class="[{ 'sidebar absolute pin-t pin-r w-full h-full fade p-2 z-50': true, show: show, close: !show }]"
    >
        <div @click="closeModal()" class="bg-backdrop absolute pin-t pin-l w-full h-full"></div>
        <div class="sidebar-content absolute pin-t h-full flex flex-col bg-white">
            <div class="sidebar-header flex justify-between items-center p-4">
                <h3 class="m-0 shadow-text-none">{{ $t('quiz_creator_log_header') }}</h3>
                <div class="close-btn-box relative flex justify-center items-center">
                    <i @click="closeModal()" class="fas fa-times text-xl shadow-text-none cursor-pointer"></i>
                </div>
            </div>
            <div class="sidebar-body flex-1 p-4 overflow-auto font-size-14">
                <div v-for="(item, index) in activityLogs" :key="index" class="timeline flex pl-4">
                    <div class="flex flex-col items-center">
                        <div class="dot w-4 h-4 rounded-full"></div>
                        <div v-if="index !== activityLogs.length - 1" class="line flex-1"></div>
                    </div>
                    <div class="timeline-content flex flex-col">
                        <div>
                            <div class="timeline-user flex">
                                <img
                                    v-if="item.imageProfile"
                                    @error="onImageError"
                                    :src="item.imageProfile"
                                    alt="profile-image"
                                    class="avatar self-center object-cover"
                                />
                                <div
                                    v-else
                                    class="avatar w-full h-full flex items-center justify-center text-avatar avatar-color"
                                >
                                    {{ profileText(nameText(item.name, item.userId), item.userId) }}
                                </div>
                                <div>
                                    <span class="font-bold shadow-text-none">{{
                                        nameText(item.name, item.userId)
                                    }}</span>
                                    <p class="shadow-text-none">{{ item.createdAtReadable }}</p>
                                </div>
                            </div>
                        </div>
                        <div :id="`activity-${index}`" class="activity relative overflow-hidden">
                            <div>
                                <p class="shadow-text-none" v-html="activity(item)"></p>
                                <ul v-if="item.questions" class="question-list m-0">
                                    <li
                                        v-for="(title, questionIndex) in item.questions"
                                        :key="questionIndex"
                                        class="shadow-text-none"
                                    >
                                        {{ title ? title : emptyTitle }}
                                    </li>
                                </ul>
                                <div
                                    v-if="item.showSeeMore"
                                    class="see-more absolute pin-b flex justify-center items-end w-full h-10"
                                >
                                    <span
                                        @click="onCollapse(item, index)"
                                        class="underline cursor-pointer shadow-text-none"
                                    >
                                        {{
                                            $t(
                                                item.isCollapsed
                                                    ? 'quiz_creator_log_see_more'
                                                    : 'quiz_creator_log_see_less'
                                            )
                                        }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-if="!activityLogs.length && !showLoader"
                    class="h-full w-full flex items-center justify-center flex-col"
                >
                    <p class="text-center">
                        <i class="fas fa-scroll font-size-18 shadow-text-none"></i>
                    </p>
                    <p class="text-center mt-2 shadow-text-none">
                        {{ $t('quiz_creator_log_empty') }}
                    </p>
                </div>
                <div v-if="showLoader" class="flex justify-center items-center mb-4">
                    <QuizLoader />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { currentUser } from '@/lib/user'
import QuizLoader from '@/components/quiz-creator/QuizLoader'
export default {
    components: {
        QuizLoader
    },

    props: {
        id: {
            type: String,
            required: true
        }
    },

    data() {
        return {
            show: true,
            showLoader: true,
            fetching: false,
            total: 0,
            activityLogs: [],
            currentPage: 1,
            emptyTitle: 'Untitled'
        }
    },

    mounted() {
        this.fetchLogs()
        document.querySelector('.sidebar-body').addEventListener('scroll', this.handleFetch)
    },

    methods: {
        closeModal() {
            this.show = false

            setTimeout(() => {
                this.$emit('close')
            }, 300)
        },

        async fetchLogs() {
            if (this.fetching || this.currentPage > this.pageCount) return

            this.fetching = true

            try {
                const logs = await this.$store.dispatch('getQuizLogs', {
                    id: this.id,
                    page: this.currentPage
                })

                if (!logs.data.length) {
                    this.showLoader = false
                    this.fetching = false
                    document.querySelector('.sidebar-body').removeEventListener('scroll', this.handleFetch)
                    return
                }

                for (const log of logs.data) {
                    if (['create_quiz', 'update_setting'].includes(log.action)) {
                        if (log.action === 'update_setting') {
                            if (log.payload.updateTitle) {
                                this.activityLogs.push({
                                    action: log.action,
                                    previousTitle: log.payload.updateTitle.previousTitle,
                                    newTitle: log.payload.updateTitle.newTitle,
                                    userId: log.user._id.$oid,
                                    name: log.user.name,
                                    imageProfile: log.user.image_profile,
                                    createdAtReadable: log.created_at_readable,
                                    showSeeMore: false,
                                    isCollapsed: true
                                })
                            }

                            if (log.payload.isUpdateDetail) {
                                this.activityLogs.push({
                                    action: log.action,
                                    userId: log.user._id.$oid,
                                    name: log.user.name,
                                    imageProfile: log.user.image_profile,
                                    createdAtReadable: log.created_at_readable,
                                    showSeeMore: false,
                                    isCollapsed: true
                                })
                            }

                            continue
                        }

                        this.activityLogs.push({
                            action: log.action,
                            userId: log.user._id.$oid,
                            name: log.user.name,
                            imageProfile: log.user.image_profile,
                            createdAtReadable: log.created_at_readable,
                            showSeeMore: false,
                            isCollapsed: true
                        })
                    }

                    if (log.action === 'update_status') {
                        this.activityLogs.push({
                            action: log.action,
                            userId: log.user._id.$oid,
                            name: log.user.name,
                            imageProfile: log.user.image_profile,
                            newStatus: log.payload.newStatus,
                            previousStatus: log.payload.previousStatus,
                            createdAtReadable: log.created_at_readable,
                            showSeeMore: false,
                            isCollapsed: true
                        })
                    }

                    if (['set_quiz_to_school', 'set_quiz_to_country'].includes(log.action)) {
                        this.activityLogs.push({
                            action: log.action,
                            userId: log.user._id.$oid,
                            name: log.user.name,
                            imageProfile: log.user.image_profile,
                            newStatus: log.payload.newStatus,
                            previousStatus: log.payload.previousStatus,
                            createdAtReadable: log.created_at_readable,
                            showSeeMore: false,
                            isCollapsed: true
                        })
                    }

                    if (['create_question', 'update_question'].includes(log.action)) {
                        for (const question of log.payload.questions) {
                            if (log.action === 'update_question') {
                                if (question.updateTitle) {
                                    this.activityLogs.push({
                                        action: log.action,
                                        previousTitle: question.updateTitle.previousTitle,
                                        newTitle: question.updateTitle.newTitle,
                                        userId: log.user._id.$oid,
                                        name: log.user.name,
                                        imageProfile: log.user.image_profile,
                                        createdAtReadable: log.created_at_readable,
                                        showSeeMore: false,
                                        isCollapsed: true
                                    })
                                }

                                if (question.isUpdateDetail) {
                                    this.activityLogs.push({
                                        action: log.action,
                                        title: question.title,
                                        userId: log.user._id.$oid,
                                        name: log.user.name,
                                        imageProfile: log.user.image_profile,
                                        createdAtReadable: log.created_at_readable,
                                        showSeeMore: false,
                                        isCollapsed: true
                                    })
                                }

                                continue
                            }

                            this.activityLogs.push({
                                action: log.action,
                                title: question.title,
                                userId: log.user._id.$oid,
                                name: log.user.name,
                                imageProfile: log.user.image_profile,
                                createdAtReadable: log.created_at_readable,
                                showSeeMore: false,
                                isCollapsed: true,
                                translateType: question.translateType
                            })
                        }
                    }

                    if (['clone_question', 'delete_question'].includes(log.action)) {
                        this.activityLogs.push({
                            action: log.action,
                            userId: log.user._id.$oid,
                            name: log.user.name,
                            imageProfile: log.user.image_profile,
                            createdAtReadable: log.created_at_readable,
                            questions: [...log.payload.questions.map(question => question.title)],
                            showSeeMore: false,
                            isCollapsed: true
                        })
                    }

                    if (log.action === 'clone_quiz') {
                        this.activityLogs.push({
                            action: log.action,
                            userId: log.user._id.$oid,
                            name: log.user.name,
                            imageProfile: log.user.image_profile,
                            createdAtReadable: log.created_at_readable,
                            title: log.payload.title,
                            showSeeMore: false,
                            isCollapsed: true
                        })
                    }

                    if (log.action === 'update_share_setting') {
                        const payload = {
                            action: log.action,
                            userId: log.user._id.$oid,
                            name: log.user.name,
                            imageProfile: log.user.image_profile,
                            createdAtReadable: log.created_at_readable,
                            type: log.payload.type,
                            previousCanRead: log.payload.previousCanRead,
                            newCanRead: log.payload.newCanRead,
                            previousCanEdit: log.payload.previousCanEdit,
                            newCanAccessReport: log.payload.newCanAccessReport,
                            previousCanAccessReport: log.payload.previousCanAccessReport,
                            newCanEdit: log.payload.newCanEdit,
                            showSeeMore: false,
                            isCollapsed: true
                        }

                        this.activityLogs.push(payload)
                    }
                }

                await this.$nextTick()

                this.pageCount = logs.page_count

                this.showLoader = this.currentPage < this.pageCount ? true : false

                const activityElements = document.querySelectorAll('.activity')

                for (const [index, element] of activityElements.entries()) {
                    const child = element.querySelector(':scope > div')

                    if (child.offsetHeight > 120) {
                        element.style.height = '120px'
                        this.activityLogs[index].showSeeMore = true
                    }
                }
            } catch {
                return false
            } finally {
                this.fetching = false
            }
        },

        activity(log) {
            switch (log.action) {
                case 'create_quiz':
                    return this.$t('quiz_creator_log_message_create_quiz')

                case 'update_setting':
                    if (typeof log.previousTitle !== 'undefined' && typeof log.newTitle !== 'undefined') {
                        return this.$t('quiz_creator_log_message_update_quiz_title', {
                            previousTitle: log.previousTitle ? log.previousTitle : this.emptyTitle,
                            newTitle: log.newTitle ? log.newTitle : this.emptyTitle
                        })
                    }
                    return this.$t('quiz_creator_log_message_update_quiz')

                case 'update_status':
                    return this.$t('quiz_creator_log_message_update_quiz_status', {
                        previousStatus: this.$t(
                            log.previousStatus ? 'quiz_creator_log_message_active' : 'quiz_creator_log_message_inactive'
                        ),
                        newStatus: this.$t(
                            log.newStatus ? 'quiz_creator_log_message_active' : 'quiz_creator_log_message_inactive'
                        )
                    })

                case 'set_quiz_to_school':
                    return this.$t('quiz_creator_log_message_update_show_school_list', {
                        previousStatus: this.$t(
                            log.previousStatus ? 'quiz_creator_log_message_active' : 'quiz_creator_log_message_inactive'
                        ),
                        newStatus: this.$t(
                            log.newStatus ? 'quiz_creator_log_message_active' : 'quiz_creator_log_message_inactive'
                        )
                    })

                case 'set_quiz_to_country':
                    return this.$t('quiz_creator_log_message_update_show_country_list', {
                        previousStatus: this.$t(
                            log.previousStatus ? 'quiz_creator_log_message_active' : 'quiz_creator_log_message_inactive'
                        ),
                        newStatus: this.$t(
                            log.newStatus ? 'quiz_creator_log_message_active' : 'quiz_creator_log_message_inactive'
                        )
                    })

                case 'create_question':
                    return this.$t('quiz_creator_log_message_create_question', {
                        translateType: this.$t(log.translateType),
                        title: log.title ? log.title : this.emptyTitle
                    })

                case 'update_question':
                    if (typeof log.previousTitle !== 'undefined' && typeof log.newTitle !== 'undefined') {
                        return this.$t('quiz_creator_log_message_update_question_title', {
                            previousTitle: log.previousTitle ? log.previousTitle : this.emptyTitle,
                            newTitle: log.newTitle ? log.newTitle : this.emptyTitle
                        })
                    }
                    return this.$t('quiz_creator_log_message_update_question', {
                        title: log.title ? log.title : this.emptyTitle
                    })

                case 'clone_question':
                    return this.$t('quiz_creator_log_message_clone', { count: log.questions.length })

                case 'delete_question':
                    return this.$t('quiz_creator_log_message_delete', { count: log.questions.length })

                case 'clone_quiz':
                    return this.$t('quiz_creator_log_message_clone_quiz', {
                        title: log.title ? log.title : this.emptyTitle
                    })

                case 'update_share_setting':
                    if (log.type === 'update_share_setting_access_report') {
                        if (log.newCanAccessReport) {
                            return this.$t('quiz_creator_log_message_update_share_setting_can_access_report')
                        }

                        return this.$t('quiz_creator_log_message_update_share_setting_remove_access_report')
                    }

                    if (!log.newCanRead && !log.newCanEdit)
                        return this.$t('quiz_creator_log_message_update_share_setting_empty')

                    if (log.type === 'update_share_setting_read')
                        return this.$t('quiz_creator_log_message_update_share_setting_read')

                    if (log.type === 'update_share_setting_edit') {
                        if (log.newCanRead && !log.newCanEdit) {
                            return this.$t('quiz_creator_log_message_update_share_setting_read')
                        }

                        return this.$t('quiz_creator_log_message_update_share_setting_edit')
                    }

                    return ''

                default:
                    break
            }
        },

        profileText(name, userId) {
            if (currentUser().id === userId) {
                name = currentUser().name
            }

            const nameArr = name.split(' ')
            const firstIndex = 0
            const lastIndex = nameArr.length - 1
            const firstLetter = nameArr[firstIndex].split('')[0]

            let lastLetter = ''

            if (lastIndex > 0) {
                lastLetter = nameArr[lastIndex].split('')[0]
            }

            return `${firstLetter}${lastLetter}`
        },

        onCollapse(item, index) {
            item.isCollapsed = !item.isCollapsed

            const activityElement = document.querySelector(`#activity-${index}`)

            if (item.isCollapsed) {
                activityElement.style.height = '120px'
            } else {
                const questionListElement = document.querySelector(`#activity-${index} > div`)
                activityElement.style.height = `${questionListElement.offsetHeight + 40}px`
            }
        },

        handleFetch() {
            const sidebarBody = document.querySelector('.sidebar-body')

            if (sidebarBody.scrollTop + sidebarBody.clientHeight >= sidebarBody.scrollHeight) {
                this.currentPage++
                this.fetchLogs()
            }
        },

        onImageError(event) {
            event.target.onerror = null
            event.target.src = require('@/assets/avatar.jpg')
        },

        nameText(name, userId) {
            if (currentUser().id === userId) {
                return this.$t('quiz_creator_log_you')
            }

            const matches = name.match(/-/g)
            const feideFormat = matches && matches.length >= 3 ? true : false

            if (feideFormat) {
                return `FEIDE ${name.split('-')[0]}`
            }

            return name
        }
    }
}
</script>

<style lang="scss">
#quiz-creator-log-sidebar {
    --content-width: 100%;

    @media screen and (min-width: 768px) {
        --content-width: 400px;
    }

    pointer-events: none;

    .bg-backdrop {
        background-color: rgba(0, 0, 0, 0.5);
        opacity: 0;
    }

    .sidebar-content {
        width: var(--content-width);
        right: 0;

        .sidebar-header {
            border-bottom: 1px solid #ccc;

            .close-btn-box {
                width: 1.5rem;
                height: 1.5rem;
            }
        }
    }

    &.show {
        pointer-events: unset;

        .bg-backdrop {
            animation: show-backdrop 0.3s forwards ease;

            @keyframes show-backdrop {
                from {
                    opacity: 0;
                }
                to {
                    opacity: 1;
                }
            }
        }

        .sidebar-content {
            animation: slide-show 0.3s forwards ease;

            @keyframes slide-show {
                from {
                    transform: translateX(var(--content-width));
                }
                to {
                    transform: translateX(0);
                }
            }
        }
    }

    &.close {
        pointer-events: none;

        .bg-backdrop {
            animation: close-backdrop 0.3s forwards ease;

            @keyframes close-backdrop {
                from {
                    opacity: 1;
                }
                to {
                    opacity: 0;
                }
            }
        }

        .sidebar-content {
            animation: slide-close 0.3s forwards ease;

            @keyframes slide-close {
                from {
                    transform: translateX(0);
                }
                to {
                    transform: translateX(var(--content-width));
                }
            }
        }
    }

    .timeline {
        column-gap: 1rem;
        min-height: 140px;

        &:first-child {
            margin-top: 12px;
        }

        .dot {
            background-color: var(--ui-primary-color);
        }

        .line {
            width: 4px;
            background-color: var(--ui-secondary-color);
        }

        .timeline-content {
            row-gap: 1rem;
            margin: -12px 0 28px 0;

            .timeline-user {
                column-gap: 0.5rem;

                .avatar {
                    width: 40px;
                    height: 40px;
                    border-radius: 100%;
                    text-decoration: none;
                    text-shadow: none;
                    font-weight: bold;
                    font-size: 1.2rem;
                    border: 3px solid #fff;
                    box-shadow: 0px 0px 6px rgba(0, 0, 0, 0.3);
                }
            }

            .activity {
                // height: 120px;
                transition: height 0.3s;

                .create {
                    font-weight: bold;
                    color: rgba(153, 0, 255, 1);
                }

                .update {
                    font-weight: bold;
                    color: rgba(23, 162, 184, 1);
                }

                .clone {
                    font-weight: bold;
                    color: rgba(255, 193, 7, 1);
                }

                .delete {
                    font-weight: bold;
                    color: rgba(220, 53, 69, 1);
                }

                .active {
                    font-weight: bold;
                    color: rgba(40, 167, 69, 1);
                }

                .inactive {
                    font-weight: bold;
                    color: rgba(220, 53, 69, 1);
                }

                .change-from {
                    font-weight: bold;
                    color: rgba(220, 53, 69, 1);
                }

                .change-to {
                    font-weight: bold;
                    color: rgba(40, 167, 69, 1);
                }

                .see-more {
                    box-shadow: 0px -60px 20px -30px rgba(255, 255, 255, 1) inset;
                    -webkit-box-shadow: 0px -60px 20px -30px rgba(255, 255, 255, 1) inset;
                    -moz-box-shadow: 0px -60px 20px -30px rgba(255, 255, 255, 1) inset;
                }
            }
        }
    }
}
</style>
